import {
  ArrowLongLeftIcon,
  ArrowPathIcon,
  ArrowUpTrayIcon,
  Button,
  CheckBox,
  FileDrop,
  Flex,
  HelpHover,
  InputContainer,
  Menu,
  Text,
  TextField,
  toaster,
} from 'components'
import { startCase } from 'lodash-es'
import { useEffect, useState } from 'react'
import { ILeadLookupConfig, LookupOutputProp } from 'types'
import { acceptedSpreadsheets, readSpreadsheet, useTheme } from 'utils'
import { SmartColumnConfigDataMapper } from '.'

interface Props {
  leadLookUpConfig: Partial<ILeadLookupConfig>
  momentifyFields: string[]
  updateLocalLookUpConfig: (leadLookUpConfig: Partial<ILeadLookupConfig>) => void
  onDataReset: () => void
  rawDbData: Record<string, string>[]
  setRawDbData: (data: Record<string, string>[]) => void
  dbFields?: string[]
  setDbFields: (fields: string[]) => void
  setDbInputProps: (dbInputProps: string[]) => void
  setLookupOutputProps: React.Dispatch<React.SetStateAction<LookupOutputProp[]>>
  disableInputs: boolean
  setSaveEnabled: (enable: boolean) => void
  pageIsLoading: (loading: boolean) => void
}

export default function SmartColumnConfigEditor({
  leadLookUpConfig,
  momentifyFields,
  updateLocalLookUpConfig,
  onDataReset,
  rawDbData,
  setRawDbData,
  setDbFields,
  setDbInputProps,
  setLookupOutputProps,
  disableInputs,
  pageIsLoading,
  dbFields,
  setSaveEnabled,
}: Props) {
  const [buffer, setBuffer] = useState<ArrayBuffer>()
  const [dbTabs, setDbTabs] = useState<string[]>([])
  const [customOptions, setCustomOptions] = useState<{ label: string; value: string }[]>([])
  const [isAddingCustom, setIsAddingCustom] = useState(false)
  const [customValue, setCustomValue] = useState('')

  const { theme } = useTheme()
  const bgColor = theme === 'light' ? 'rgba(23, 23, 23, 0.1)' : 'rgba(255, 255, 255, 0.2)'
  const existingOutputProps: LookupOutputProp[] =
    leadLookUpConfig?.outputProperties?.map((row, index) => ({
      index: index,
      dbField: row.dbField,
      dbFieldName: row.name,
      hasChart: row.hasChart,
    })) || []

  const allMenuOptions = [
    ...momentifyFields.map(property => ({
      label: startCase(property),
      value: property,
    })),
    ...customOptions,
  ]

  useEffect(() => {
    if (!buffer) return
    ;(async () => {
      try {
        pageIsLoading(true)
        const { SheetNames } = await readSpreadsheet(buffer)
        setDbTabs(SheetNames)
        pageIsLoading(false)
      } catch (err) {
        console.error('Error reading spreadsheet:', err)
      }
    })()
  }, [buffer])

  useEffect(() => {
    if (!rawDbData) setDbTabs([])
  }, [rawDbData])

  async function handleDataUpload(files: File[]) {
    try {
      if (!files?.length) throw 'File type not accepted'
      pageIsLoading(true)
      setBuffer(await files[0].arrayBuffer())
      pageIsLoading(false)
    } catch (error: any) {
      console.error(error)
      toaster.create({ title: error.message, type: 'error' })
    }
  }

  function handleAddCustom() {
    const value = customValue.trim().toLowerCase()
    if (!value) return

    const alreadyExists =
      momentifyFields.includes(value) || customOptions.some(opt => opt.value === value)
    if (alreadyExists) {
      toaster.create({
        title: 'Field already exists',
        type: 'warning',
      })
      return
    }
    const newOption = {
      label: startCase(value),
      value,
    }
    setCustomOptions(prev => [...prev, newOption])
    updateLocalLookUpConfig({
      ...leadLookUpConfig,
      inputProperties: [...(leadLookUpConfig.inputProperties ?? []), value],
    })
    setCustomValue('')
    setIsAddingCustom(false)
  }

  return (
    <Flex direction="column" gap={3}>
      <Flex direction="row" gap={10} p={1}>
        <Flex direction="column" w={{ base: '100%', sm: '400px' }} gap={3}>
          <TextField
            label="Title"
            value={leadLookUpConfig?.title || ''}
            disabled={disableInputs}
            placeholder="Enter title..."
            onChange={e => updateLocalLookUpConfig({ ...leadLookUpConfig, title: e.target.value })}
          />
          <TextField
            label="Description"
            value={leadLookUpConfig?.description || ''}
            isArea
            disabled={disableInputs}
            placeholder="Enter description..."
            onChange={e =>
              updateLocalLookUpConfig({ ...leadLookUpConfig, description: e.target.value })
            }
          />
        </Flex>
        <Flex gap={6} direction="column" align="flex-end">
          <Flex gap={3} align="flex-start">
            <Flex gap={3} align="flex-end">
              {leadLookUpConfig?.outputProperties ? (
                <InputContainer label="Input Field">
                  <Text bg={bgColor} {...readOnlyTextProps}>
                    {leadLookUpConfig.inputProperties?.map(prop => startCase(prop)).join(', ')}
                  </Text>
                </InputContainer>
              ) : (
                <Flex gap={3} align="flex-end">
                  <Menu
                    variant="outline"
                    menuProps={{ positioning: { sameWidth: true } }}
                    portal
                    w={optionWidths}
                    borderRadius="10px"
                    label="Momentify Field"
                    disabled={disableInputs}
                    value={leadLookUpConfig?.inputProperties!}
                    isMulti
                    options={allMenuOptions}
                    onChange={values => {
                      updateLocalLookUpConfig({
                        ...leadLookUpConfig,
                        inputProperties: values,
                      })
                    }}
                  />

                  {!isAddingCustom ? (
                    <Flex gap={0} p={0.5} align="center">
                      <Button
                        variant="ghost"
                        onClick={() => setIsAddingCustom(true)}
                        disabled={disableInputs}
                      >
                        + Add custom field
                      </Button>
                      <HelpHover text="If there are custom fields that will generated on the lead from trait selections, they can be added here" />
                    </Flex>
                  ) : (
                    <Flex gap={2} p={0.5} align="center" justify="center">
                      <TextField
                        autoFocus
                        size="sm"
                        placeholder="Enter custom field..."
                        value={customValue}
                        onChange={e => setCustomValue(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            handleAddCustom()
                          }
                        }}
                      />
                      <Button onClick={handleAddCustom}>Add</Button>
                    </Flex>
                  )}
                </Flex>
              )}

              {leadLookUpConfig.outputProperties &&
                Object.values(leadLookUpConfig?.outputProperties || [])[0]?.name !== '' && (
                  <ArrowLongLeftIcon transform="rotateY(180deg)" mb={2} />
                )}
            </Flex>
            {leadLookUpConfig.outputProperties &&
              Object.values(leadLookUpConfig?.outputProperties || [])[0]?.name !== '' && (
                <Flex direction="column" gap={2} textStyle="bodySmall">
                  <Flex gap={2} opacity={0.7} alignItems="center" px={1}>
                    <Text w="130px">Output Fields</Text>
                    <Flex gap={1} align="center">
                      <Text>Analytics</Text>
                      <HelpHover text="Values from selected fields will be used for event analytics and can be viewed as charts in the event analytics page." />
                    </Flex>
                  </Flex>
                  <Flex
                    direction="column"
                    border={`1px solid ${bgColor}`}
                    borderRadius="10px"
                    p={1}
                  >
                    {leadLookUpConfig?.outputProperties.map(({ name, hasChart }) => {
                      return (
                        <Flex key={name} gap={3} p={2} align="center">
                          <Text textStyle="bodySmall" fontWeight="normal" w="130px">
                            {startCase(name)}
                          </Text>
                          <Flex flex={1} justify="center">
                            <CheckBox
                              checked={hasChart}
                              onChange={() => {
                                const updatedOutputProperties =
                                  leadLookUpConfig.outputProperties?.map(prop =>
                                    prop.name === name ? { ...prop, hasChart: !hasChart } : prop
                                  )
                                updateLocalLookUpConfig({
                                  ...leadLookUpConfig,
                                  outputProperties: updatedOutputProperties,
                                })
                              }}
                            />
                          </Flex>
                        </Flex>
                      )
                    })}
                  </Flex>
                </Flex>
              )}
          </Flex>
          <FileDrop onDrop={handleDataUpload} options={{ accept: acceptedSpreadsheets }}>
            {({ open }) => (
              <Button
                variant="outline"
                disabled={disableInputs || !leadLookUpConfig?.inputProperties}
                leftIcon={dbFields ? <ArrowPathIcon /> : <ArrowUpTrayIcon />}
                onClick={() => {
                  open()
                  onDataReset()
                }}
              >
                {`${dbFields || leadLookUpConfig?.outputProperties ? 'Replace' : 'Add'} Data`}
              </Button>
            )}
          </FileDrop>
        </Flex>
      </Flex>
      {dbTabs.length > 0 && (
        <SmartColumnConfigDataMapper
          inputsDisabled={disableInputs}
          dbTabs={dbTabs}
          setRawDbData={setRawDbData}
          dbFields={dbFields}
          setDbFields={setDbFields}
          dbInputProps={leadLookUpConfig.dbInputProperties}
          setDbInputProps={setDbInputProps}
          lookupOutputProps={existingOutputProps}
          setLookupOutputProps={setLookupOutputProps}
          buffer={buffer}
          momentifyFields={momentifyFields}
          setSaveEnabled={setSaveEnabled}
          pageIsLoading={pageIsLoading}
        />
      )}
    </Flex>
  )
}

const optionWidths = { base: '100%', sm: '250px' }

const readOnlyTextProps = {
  textStyle: 'bodySmall',
  borderRadius: 'sm',
  p: 3,
}
